import {MountResponseDTO, ProductAffiliateResponseDTO, ProductResponseDTO} from "@shared/DTO";
import {NAMES} from "@/scripts/components/ab-lens-compatibility/constants/abLensCompatibility.constants";
import {CompatibilityLevel} from "@/scripts/components/ab-lens-compatibility/abLensCompatibility.enums";
import {Mount} from "@shared/enums";
import {createDomElementHandler} from "@/scripts/utils/dom.utils";

export const buildMainArea = (
  lens: ProductResponseDTO,
  lensMountsRules: {mount: MountResponseDTO, affiliate: ProductAffiliateResponseDTO}[]) => {
  const fragment = document.createDocumentFragment();
  const descriptionEl = buildBaseDescription(lens, lensMountsRules);
  const tableEl = buildTable(lensMountsRules);
  const feedbackWrapperEl = createDomElementHandler('div', NAMES.CLASS_FEEDBACK_WRAPPER);
  fragment.appendChild(descriptionEl);
  fragment.appendChild(tableEl);
  fragment.appendChild(feedbackWrapperEl);
  return fragment;
};

export const buildCameraCheckArea = () => {
  const wrapper = createDomElementHandler('div', NAMES.CLASS_SEARCH_WRAPPER);
  const openPanelCtaEl = buildOpenPanelCta();
  const closePanelMsgEl = buildCheckCloseMessage();
  const instructionsEl = buildOpenPanelInstructions();
  wrapper.appendChild(openPanelCtaEl);
  wrapper.appendChild(closePanelMsgEl);
  wrapper.appendChild(instructionsEl);
  return wrapper;
}

export const buildBaseDescription = (
  lens: ProductResponseDTO,
  lensMountsRules: { mount: MountResponseDTO, affiliate: ProductAffiliateResponseDTO }[]
) => {

  const lensName = `<strong>${lens.productData.names?.at(0)}</strong>`;
  const mountName = `<strong>${lensMountsRules[0].mount.name}</strong>`;

  const introString = lensMountsRules.length > 1 ?
    `L'obiettivo ${lensName} è disponibile in ${lensMountsRules.length} diverse versioni, compatibili con gli attacchi indicati nella tabella qui sotto.` :
    `L'obiettivo ${lensName} è compatibile con  le fotocamere ad attacco ${mountName}.`;

  return createDomElementHandler('p', NAMES.CLASS_INTRO, undefined, introString);
}

const buildTable = (
  lensMountsRules: { mount: MountResponseDTO, affiliate: ProductAffiliateResponseDTO }[]
) => {

  const hasCtas = lensMountsRules
    ?.some(({affiliate}) =>
      affiliate?.url?.length > 0
    );

  const tableContent = `
    ${buildTableHead(hasCtas)}
    <tbody class="${NAMES.CLASS_TABLE_BODY}">
      ${lensMountsRules.map(mountRule => buildBodyRow(mountRule, hasCtas)).join('')}
    </tbody>   
  `;

  return createDomElementHandler('table', NAMES.CLASS_TABLE, undefined, tableContent);
}

const buildOpenPanelCta = () => {
  const classes = [NAMES.CLASS_MSG, NAMES.CLASS_MSG_OPEN_PANEL];
  const content = `
    <a href="javascript:void(null)" class="${NAMES.TRIGGER_CHECK_CAMERA_OPEN}">
       Verifica compatibilità con una fotocamera <span class="icon-arrow-to-down"></span>
    </a>
  `;
  return createDomElementHandler('p', classes, undefined, content);
};

const buildOpenPanelInstructions = () => {
  const classes = [NAMES.CLASS_MSG, NAMES.CLASS_MSG_INSTRUCTIONS];
  const content = `  
    Cerca la tua fotocamera fra quelle qui sotto e poi <strong>fai click sul nome del modello</strong> per verificare se è compatibile con l'obiettivo.
    <span class="icon-info"></span>
  `;
  return createDomElementHandler('p', classes, undefined, content);
};

const buildCheckCloseMessage = () => {
  const classes = [NAMES.CLASS_MSG, NAMES.CLASS_MSG_CLOSE_PANEL];
  const content = `<a href="javascript:void(null)" class="${NAMES.TRIGGER_CHECK_CAMERA_CLOSE}">Chiudi pannello di ricerca <span class="icon-arrow-to-up"></span></a>`;
  return createDomElementHandler('p', classes, undefined, content);
}

export const buildFeedbackMessage = (
  lens: ProductResponseDTO,
  camera: ProductResponseDTO,
  compatibility: { mount: Mount, compatibilityLevel: CompatibilityLevel }
) => {

  const compatibilityLevel = compatibility.compatibilityLevel;
  const lensName = `<strong>${lens.productData.names?.at(0)}</strong>`;
  const cameraName = `<strong>${camera.productData.names?.at(0)}</strong>`;

  let message = '';
  let recap = '';

  switch (compatibilityLevel) {
    case CompatibilityLevel.FULL:
      recap = 'Compatibilità piena';
      message = `L'obiettivo ${lensName} è <strong>pienamente compatibile</strong> con la fotocamera ${cameraName}.`;
      break;
    case CompatibilityLevel.PARTIAL:
      recap = 'Compatibilità parziale';
      message = `L'obiettivo ${lensName} è <strong>parzialmente compatibile</strong> con la fotocamera ${cameraName}. Potrebbero esserci delle limitazioni nell'uso dell'obiettivo, ad esempio in termini di copertura del sensore o funzionalità supportate.`;
      break;
    case CompatibilityLevel.NOT_COMPATIBLE:
      recap = 'Nessuna compatibilità';
      message = `L'obiettivo ${lensName} <strong>non è compatibile</strong> con la fotocamera ${cameraName}.`;
      break;
  }

  const compatibilityCssClassesMap: Record<CompatibilityLevel, string> = {
    [CompatibilityLevel.FULL]: NAMES.MODIFIER_FEEDBACK_FULL,
    [CompatibilityLevel.PARTIAL]: NAMES.MODIFIER_FEEDBACK_PARTIAL,
    [CompatibilityLevel.NOT_COMPATIBLE]: NAMES.MODIFIER_FEEDBACK_NONE
  };

  const classes = [NAMES.CLASS_FEEDBACK, compatibilityCssClassesMap[compatibilityLevel]];
  const content = `    
    <span class="ab-lens-compatibility__feedback-recap">
      <strong>${recap}</strong>
    </span> 
    ${message}
  `;
  return createDomElementHandler('div', classes, undefined, content);
}

const buildBodyRow = (
  {mount, affiliate}: { mount: MountResponseDTO, affiliate: ProductAffiliateResponseDTO },
  hasCtas: boolean
) => {

  const ctaCell = hasCtas ? `
        <td class="ab-lens-compatibility__table-body-cell">
          ${affiliate?.url?.length ? `<a href="${affiliate.url}" target="_blank" rel="noopener noreferrer">
            Vedi su Amazon
          </a>` : ''}
        </td>
  ` : '';

  return `
    <tr class="ab-lens-compatibility__table-body-row" data-mount="${mount?.public_id}">
      <td class="ab-lens-compatibility__table-body-cell">${mount.name}</td>
      <td class="ab-lens-compatibility__table-body-cell">${mount.description}</td>
      ${ctaCell}
    </tr>
  `;
}

const buildTableHead = (
  hasCtas: boolean
) => {
  const ctaCell = hasCtas ? `
        <th class="ab-lens-compatibility__table-body-cell">
          Acquista
        </th>
  ` : '';
  return `
    <thead class="ab-lens-compatibility__table-head">
      <tr class="ab-lens-compatibility__table-head-row">
        <th class="ab-lens-compatibility__table-body-cell">
          Attacco / formato
        </th>
        <th class="ab-lens-compatibility__table-body-cell">
          Descrizione
        </th>            
        ${ctaCell}
      </tr>
    </thead>
  `
}
